import { Check, X } from "lucide-react";
import { useCanAccess, useGetIdentity, useTranslate } from "ra-core";
import { Link } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { reportsPath } from "../reports/reportTabs";
import { routePaths } from "../root/routePaths";
import { useTutorialProgress } from "../tutorial/useTutorialProgress";

// «Kom i gang» øverst på Oversikt (PRD US23.1): tre handlinger som passer
// rollen, med lenker til de faktiske sidene. Klikk huker av handlingen;
// kortet forsvinner når alle tre er gjort eller brukeren skjuler det.
// Fremdriften følger brukeren (useTutorialProgress), så kortet ikke dukker
// opp igjen i en ny nettleser.
export const HIDDEN_ID = "oppstart:skjult";

type Action = { id: string; to: string; key: string; fallback: string };

const adminActions: Action[] = [
  {
    id: "oppstart:kunde",
    to: "/companies/create",
    key: "crm.dashboard.first_actions.admin_customer",
    fallback: "Registrer den første kunden",
  },
  {
    id: "oppstart:medarbeider",
    to: "/sales/create",
    key: "crm.dashboard.first_actions.admin_user",
    fallback: "Legg til en medarbeider",
  },
  {
    id: "oppstart:fakturagrunnlag",
    to: reportsPath("invoice_basis"),
    key: "crm.dashboard.first_actions.admin_invoice_basis",
    fallback: "Se fakturagrunnlaget for måneden",
  },
];

const employeeActions = (salesId: string | number | undefined): Action[] => [
  {
    id: "oppstart:oppgaver",
    to:
      salesId != null
        ? `/work_items?filter=${encodeURIComponent(
            JSON.stringify({ assignee_id: salesId }),
          )}`
        : "/work_items",
    key: "crm.dashboard.first_actions.my_tasks",
    fallback: "Se oppgavene dine",
  },
  {
    id: "oppstart:kontakt",
    to: "/companies",
    key: "crm.dashboard.first_actions.log_contact",
    fallback: "Logg en kontakt på en kunde",
  },
  {
    id: "oppstart:veiledning",
    to: routePaths.tutorial,
    key: "crm.dashboard.first_actions.tutorial",
    fallback: "Ta veiledningen",
  },
];

export const FirstActions = () => {
  const translate = useTranslate();
  const { identity } = useGetIdentity();
  const { canAccess: isAdmin, isPending } = useCanAccess({
    resource: "sales",
    action: "list",
  });
  const { completed, loaded, markDone } = useTutorialProgress();

  if (!loaded || isPending || completed.has(HIDDEN_ID)) return null;
  const actions = isAdmin ? adminActions : employeeActions(identity?.id);
  if (actions.every((action) => completed.has(action.id))) return null;

  return (
    <Card className="gap-3 px-5 py-4" aria-labelledby="first-actions-title">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h2 id="first-actions-title" className="font-semibold">
            {translate("crm.dashboard.first_actions.title", {
              _: "Kom i gang",
            })}
          </h2>
          <p className="text-sm text-muted-foreground">
            {translate("crm.dashboard.first_actions.subtitle", {
              _: "Tre ting å gjøre først i Mimin.",
            })}
          </p>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-8 shrink-0"
          aria-label={translate("crm.dashboard.first_actions.hide", {
            _: "Skjul Kom i gang",
          })}
          onClick={() => markDone(HIDDEN_ID)}
        >
          <X className="size-4" />
        </Button>
      </div>
      <ol className="grid gap-2 sm:grid-cols-3">
        {actions.map((action, index) => {
          const done = completed.has(action.id);
          return (
            <li key={action.id}>
              <Link
                to={action.to}
                onClick={() => markDone(action.id)}
                className="flex h-full items-center gap-3 rounded-lg border px-3 py-2.5 text-sm transition-colors hover:bg-accent"
              >
                <span
                  aria-hidden
                  className={
                    done
                      ? "grid size-6 shrink-0 place-items-center rounded-full bg-primary text-primary-foreground"
                      : "grid size-6 shrink-0 place-items-center rounded-full border text-xs text-muted-foreground"
                  }
                >
                  {done ? <Check className="size-3.5" /> : index + 1}
                </span>
                <span className={done ? "text-muted-foreground" : undefined}>
                  {translate(action.key, { _: action.fallback })}
                  {done && (
                    <span className="sr-only">
                      {" "}
                      {translate("crm.dashboard.first_actions.done", {
                        _: "(gjort)",
                      })}
                    </span>
                  )}
                </span>
              </Link>
            </li>
          );
        })}
      </ol>
    </Card>
  );
};
